import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'react-toastify';
import { chatbotAdminApi } from '../../api/chatbotApi';
import type {
  ChatbotAiCatalog,
  ChatbotBotDetail,
  TenantAiKeysResponse,
} from '../../types/chatbot.types';
import {
  BotConfigurationForm,
  type BotConfigurationFormData,
} from '../../components/admin/BotConfigurationForm';
import { TenantAiKeysCard } from '../../components/admin/TenantAiKeysCard';
import { noragFormToPayload } from '../../api/noragFormPayload';
import { conciergeFormToPayload } from '../../api/conciergeFormPayload';
import { infoFieldsFormToPayload } from '../../api/infoFieldsFormPayload';
import { padChatbotInfoFields } from '@workspace/types';
import {
  hasTenantProviderKeyForCreate,
  providerKeyCreateErrorMessage,
} from '../../utils/providerKeyValidation';
import { useConfirm } from '@/contexts/confirmContext';

function toPayload(data: BotConfigurationFormData) {
  const concierge = conciergeFormToPayload(data);
  return {
    name: data.name,
    description: data.description || undefined,
    provider: data.provider,
    modelId: data.kind === 'CONCIERGE' ? data.modelId || undefined : data.modelId,
    systemPrompt: data.kind === 'CONCIERGE' ? data.systemPrompt || undefined : data.systemPrompt,
    openaiApiKey: data.openaiApiKey?.trim() || undefined,
    anthropicApiKey: data.anthropicApiKey?.trim() || undefined,
    clearOpenaiApiKey: data.clearOpenaiApiKey || undefined,
    clearAnthropicApiKey: data.clearAnthropicApiKey || undefined,
    temperature: data.temperature ? Number.parseFloat(data.temperature) : undefined,
    maxOutputTokens: data.maxOutputTokens
      ? Number.parseInt(data.maxOutputTokens, 10)
      : undefined,
    sttModelId: data.sttModelId || 'whisper-1',
    historyMessageLimit: data.historyMessageLimit
      ? Number.parseInt(data.historyMessageLimit, 10)
      : undefined,
    historyMessageMaxChars: data.historyMessageMaxChars
      ? Number.parseInt(data.historyMessageMaxChars, 10)
      : undefined,
    conversationMode: concierge.conversationMode ?? data.conversationMode,
    iconUrl: data.iconUrl || undefined,
    ...noragFormToPayload(data),
    ...concierge,
    ...infoFieldsFormToPayload(data),
    operatorIdleResumeMinutes: data.operatorIdleResumeMinutes
      ? Number.parseInt(data.operatorIdleResumeMinutes, 10)
      : undefined,
    operatorTakeoverNotice: data.operatorTakeoverNotice?.trim() || undefined,
    operatorResumeNotice: data.operatorResumeNotice?.trim() || undefined,
  };
}

export function ChatbotBotEditPage() {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const isNew = botId === 'new';
  const [deleting, setDeleting] = useState(false);

  const [catalog, setCatalog] = useState<ChatbotAiCatalog | null>(null);
  const [detail, setDetail] = useState<ChatbotBotDetail | null>(null);
  const [tenantKeys, setTenantKeys] = useState<TenantAiKeysResponse | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const data = await chatbotAdminApi.getBotAiCatalog();
        setCatalog(data);
      } catch {
        toast.error('No se pudo cargar el catálogo de modelos y prompts');
      }
    };
    loadCatalog();
  }, []);

  useEffect(() => {
    if (isNew) {
      return;
    }
    const loadBot = async () => {
      try {
        const data = await chatbotAdminApi.getBotDetail(botId!);
        setDetail(data);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Error al cargar bot';
        toast.error(message);
        navigate('/admin/chatbot/bots');
      } finally {
        setLoading(false);
      }
    };
    loadBot();
  }, [botId, isNew, navigate]);

  const handleDelete = async () => {
    if (isNew || !botId || !detail) {
      return;
    }
    const confirmed = await confirm({
      title: 'Eliminar bot',
      message: `Se elimina «${detail.bot.name}» junto con sus conversaciones y el vínculo con las instancias. No se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }
    setDeleting(true);
    try {
      await chatbotAdminApi.deleteIndependentBot(botId);
      toast.success('Bot eliminado');
      navigate('/admin/chatbot/bots');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error al eliminar bot';
      toast.error(message);
      setDeleting(false);
    }
  };

  const handleSubmit = async (data: BotConfigurationFormData) => {
    if (
      isNew &&
      data.kind !== 'CONCIERGE' &&
      !hasTenantProviderKeyForCreate(data.provider, data, tenantKeys)
    ) {
      toast.error(providerKeyCreateErrorMessage(data.provider));
      return;
    }

    setSaving(true);
    try {
      const payload = toPayload(data);
      if (isNew) {
        await chatbotAdminApi.createIndependentBot(payload);
        toast.success('Bot creado');
        navigate('/admin/chatbot/bots');
      } else {
        await chatbotAdminApi.updateIndependentBot(botId!, payload);
        toast.success('Configuración guardada');
        const refreshed = await chatbotAdminApi.getBotDetail(botId!);
        setDetail(refreshed);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error al guardar bot';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const initialValues: Partial<BotConfigurationFormData> | undefined = isNew
    ? undefined
    : detail
      ? {
          name: detail.bot.name,
          description: detail.bot.description || '',
          provider: detail.bot.provider,
          modelId: detail.bot.modelId || 'gpt-4o-mini',
          systemPrompt: detail.bot.systemPrompt || '',
          temperature: detail.bot.temperature?.toString() || '0.7',
          maxOutputTokens: detail.bot.maxOutputTokens?.toString() || '4096',
          sttModelId: detail.bot.sttModelId || 'whisper-1',
          historyMessageLimit: detail.bot.historyMessageLimit?.toString() || '40',
          historyMessageMaxChars: detail.bot.historyMessageMaxChars?.toString() || '8000',
          conversationMode: detail.bot.conversationMode,
          iconUrl: detail.bot.iconUrl || '',
          kind: detail.bot.kind ?? 'STANDARD',
          welcomeText: detail.bot.concierge?.welcomeText ?? '',
          webMandatory: detail.bot.concierge?.webMandatory ?? false,
          idleMinutes: detail.bot.concierge?.idleMinutes?.toString() ?? '30',
          pendingIdleMinutes: detail.bot.concierge?.pendingIdleMinutes?.toString() ?? '240',
          escapeKeywords: detail.bot.concierge?.escapeKeywords?.join(', ') ?? '',
          classifyEnabled: detail.bot.concierge?.classifyEnabled ?? true,
          topicShiftEnabled: detail.bot.concierge?.topicShiftEnabled ?? false,
          noragEnabled: detail.bot.norag?.enabled ?? false,
          noragEmpresaId: detail.bot.norag?.empresaId?.toString() ?? '',
          noragBotId: detail.bot.norag?.botId ?? '',
          noragApiUrl: detail.bot.norag?.apiUrl ?? '',
          botInfoFieldsEnabled: detail.bot.botInfoFieldsEnabled ?? false,
          userInfoFieldsEnabled: detail.bot.userInfoFieldsEnabled ?? false,
          infoFields: padChatbotInfoFields(detail.bot.infoFields),
          operatorIdleResumeMinutes:
            detail.bot.operatorIdleResumeMinutes?.toString() ?? '30',
          operatorTakeoverNotice: detail.bot.operatorTakeoverNotice ?? '',
          operatorResumeNotice: detail.bot.operatorResumeNotice ?? '',
        }
      : undefined;

  return (
    <div className='container mx-auto py-6 space-y-6'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <Button variant='ghost' size='icon' onClick={() => navigate('/admin/chatbot/bots')}>
            <ArrowLeft className='h-4 w-4' />
          </Button>
          <div>
            <h1 className='text-2xl font-bold'>
              {isNew ? 'Nuevo bot IA' : `Configurar: ${detail?.bot.name ?? 'Bot'}`}
            </h1>
            <p className='text-muted-foreground'>
              Modelo, system prompt, memoria de sesión y credenciales por proveedor.
            </p>
          </div>
        </div>
        {!isNew && detail ? (
          <Button variant='destructive' disabled={deleting} onClick={() => void handleDelete()}>
            <Trash2 className='h-4 w-4 mr-2' />
            Eliminar
          </Button>
        ) : null}
      </div>

      {loading ? (
        <p className='text-muted-foreground'>Cargando configuración...</p>
      ) : (
        <>
          {isNew && (
            <TenantAiKeysCard
              title='API keys del tenant (fallback)'
              onKeysLoaded={setTenantKeys}
            />
          )}

          {!isNew && detail && detail.linkedInstances.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Instancias vinculadas</CardTitle>
              </CardHeader>
              <CardContent className='text-sm text-muted-foreground space-y-1'>
                {detail.linkedInstances.map(instance => (
                  <div key={instance.instanceId}>
                    {instance.name}{' '}
                    <span className='text-xs'>
                      (orden {instance.displayOrder}, {instance.isActive ? 'activo' : 'inactivo'})
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <BotConfigurationForm
            mode={isNew ? 'create' : 'edit'}
            initialValues={initialValues}
            catalog={catalog}
            aiReady={detail?.bot.aiReady}
            openaiApiKeyStatus={detail?.bot.openaiApiKey}
            anthropicApiKeyStatus={detail?.bot.anthropicApiKey}
            noragStatus={detail?.bot.norag}
            lastAuthError={detail?.lastAiAuthErrorMessage}
            editingBotId={isNew ? null : botId}
            existingConcierge={
              detail?.bot.kind === 'CONCIERGE' && botId
                ? { botId, name: detail.bot.name }
                : null
            }
            classifyNeedsModel={!detail?.bot.aiReady && !tenantKeys?.openaiApiKey.configured}
            saving={saving}
            onSubmit={handleSubmit}
            onCancel={() => navigate('/admin/chatbot/bots')}
          />
        </>
      )}
    </div>
  );
}
